import { formatARS, type BulkPriceChangeDto, type BulkPriceInput, type BulkPricePreviewDto, type PriceTargetIssue } from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, errorMessage } from '@/lib/api';
import { queryClient } from '@/lib/query';
import { cn } from '@/lib/utils';
import { formatPct, shortBatch } from './format';
import { prices, pricesKeys, revertSummary, useInvalidatePrices } from './hooks';
import { PreviewTable } from './PreviewTable';
import { OperationStep } from './steps/OperationStep';
import { ROUNDING_LABELS, RoundingStep } from './steps/RoundingStep';
import { ScopeStep, useActiveCategories } from './steps/ScopeStep';
import { STEP_FIELDS, toBulkBody, WIZARD_DEFAULTS, wizardSchema, type WizardValues } from './wizard-form';

const STEPS = ['Alcance', 'Operación', 'Redondeo', 'Revisar'] as const;
const DEFAULT_SAMPLE_CENTS = 650000;

const STALE_MESSAGE = 'Alguien cambió un precio mientras tanto, volvé a previsualizar';

/** "Deshacer" desde el toast: no depende de que el wizard siga montado. */
async function undoBatch(localId: string, batchId: string) {
  const id = toast.loading('Deshaciendo…');
  try {
    const r = await prices.revert(localId, batchId);
    toast.success(revertSummary(r), { id });
  } catch (err) {
    toast.error(errorMessage(err), { id });
  } finally {
    void queryClient.invalidateQueries({ queryKey: pricesKeys.catalog(localId) });
    void queryClient.invalidateQueries({ queryKey: pricesKeys.all(localId) });
  }
}

function invalidTargets(err: unknown): PriceTargetIssue[] {
  if (!(err instanceof ApiError) || err.code !== 'INVALID_RESULT_PRICE') return [];
  const d = err.details as { targets?: PriceTargetIssue[] } | undefined;
  return d?.targets ?? [];
}

function scopeSummary(v: WizardValues): string {
  const variants = v.includeVariants ? 'con variantes' : 'sin variantes';
  if (v.scopeMode === 'all') return `Todo el menú, ${variants}`;
  if (v.scopeMode === 'categories') {
    return `${v.categoryIds.length === 1 ? '1 categoría' : `${v.categoryIds.length} categorías`}, ${variants}`;
  }
  return `${v.itemIds.length === 1 ? '1 producto' : `${v.itemIds.length} productos`}, ${variants}`;
}

function opSummary(body: BulkPriceInput): string {
  const { type, value } = body.op;
  if (type === 'PERCENT') return `${value > 0 ? 'Subir' : 'Bajar'} ${Math.abs(value).toLocaleString('es-AR')} %`;
  if (type === 'DELTA_CENTS') return `${value > 0 ? 'Sumar' : 'Restar'} ${formatARS(Math.abs(value))}`;
  return `Precio fijo ${formatARS(value)}`;
}

function roundingSummary(body: BulkPriceInput): string {
  const r = body.rounding;
  if (!r || r.mode === 'NONE' || !r.mode) return ROUNDING_LABELS.NONE;
  return `${ROUNDING_LABELS[r.mode]} a ${formatARS(r.stepCents ?? 0)}`;
}

export function BulkWizard({ localId }: { localId: string }) {
  const [step, setStep] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [stale, setStale] = useState(false);
  const [previewBody, setPreviewBody] = useState<BulkPriceInput | null>(null);
  const invalidate = useInvalidatePrices(localId);
  const { categories } = useActiveCategories(localId);

  const form = useForm<WizardValues>({
    resolver: zodResolver(wizardSchema),
    defaultValues: WIZARD_DEFAULTS,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const sampleCents = useMemo(() => {
    const first = categories.flatMap((c) => c.items)[0];
    return first?.variants[0]?.priceCents ?? first?.priceCents ?? DEFAULT_SAMPLE_CENTS;
  }, [categories]);

  const preview = useMutation({
    mutationFn: (body: BulkPriceInput) => prices.preview(localId, body),
    onMutate: () => setStale(false),
  });

  const apply = useMutation({
    mutationFn: ({ body, changes }: { body: BulkPriceInput; changes: BulkPriceChangeDto[] }) =>
      prices.apply(localId, {
        ...body,
        expected: changes.map(({ targetType, id, oldPriceCents, newPriceCents }) => ({
          targetType,
          id,
          oldPriceCents,
          newPriceCents,
        })),
      }),
    onSuccess: (r) => {
      setConfirmOpen(false);
      toast.success(r.count === 1 ? 'Listo: se actualizó 1 precio' : `Listo: se actualizaron ${r.count} precios`, {
        description: `Lote ${shortBatch(r.batchId)}. Lo podés revertir desde el historial.`,
        duration: 12_000,
        action: { label: 'Deshacer', onClick: () => void undoBatch(localId, r.batchId) },
      });
      void invalidate();
      preview.reset();
      setPreviewBody(null);
      form.reset(WIZARD_DEFAULTS);
      setStep(0);
    },
    onError: (err) => {
      setConfirmOpen(false);
      if (err instanceof ApiError && err.code === 'PRICE_CHANGED') {
        setStale(true);
        toast.error(STALE_MESSAGE);
        void invalidate();
      } else {
        toast.error(errorMessage(err));
      }
    },
  });

  const runPreview = () => {
    const body = toBulkBody(form.getValues());
    setPreviewBody(body);
    preview.mutate(body);
  };

  const next = async () => {
    const ok = await form.trigger(STEP_FIELDS[step]);
    if (!ok) return;
    if (step === 2) runPreview();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => {
    if (step === 3) {
      preview.reset();
      setStale(false);
    }
    setStep((s) => Math.max(s - 1, 0));
  };

  const goTo = (target: number) => {
    if (target < step) {
      if (step === 3) preview.reset();
      setStale(false);
      setStep(target);
    }
  };

  const data: BulkPricePreviewDto | undefined = preview.data;
  const values = form.getValues();

  return (
    <Card>
      <CardContent className="grid gap-5">
        <Stepper step={step} onGoTo={goTo} />

        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (step < 3) void next();
          }}
          className="grid gap-5"
        >
          {step === 0 && <ScopeStep form={form} localId={localId} />}
          {step === 1 && <OperationStep form={form} sampleCents={sampleCents} />}
          {step === 2 && <RoundingStep form={form} sampleCents={sampleCents} />}
          {step === 3 && previewBody && (
            <div className="grid gap-4">
              <dl className="grid gap-2 rounded-lg bg-muted p-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-muted-foreground">Alcance</dt>
                  <dd className="font-medium">{scopeSummary(values)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Operación</dt>
                  <dd className="font-medium">{opSummary(previewBody)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Redondeo</dt>
                  <dd className="font-medium">{roundingSummary(previewBody)}</dd>
                </div>
              </dl>

              {stale && (
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
                  <AlertTriangle className="size-4 shrink-0 text-destructive" />
                  <span className="flex-1">{STALE_MESSAGE}.</span>
                  <Button type="button" size="sm" variant="outline" onClick={runPreview} disabled={preview.isPending}>
                    <RefreshCw /> Previsualizar de nuevo
                  </Button>
                </div>
              )}

              {preview.isPending ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                  <Spinner /> Calculando precios…
                </div>
              ) : preview.error ? (
                <PreviewError error={preview.error} onRetry={runPreview} />
              ) : data && data.count === 0 ? (
                <p className="rounded-lg border p-4 text-sm text-muted-foreground">
                  Con esta operación ningún precio cambia. Volvé atrás y probá con otro valor o redondeo.
                </p>
              ) : data ? (
                <>
                  <p className="text-sm">
                    Van a cambiar <strong>{data.count === 1 ? '1 precio' : `${data.count} precios`}</strong>, con una
                    variación promedio de <strong>{formatPct(data.avgDeltaPct)}</strong>.
                  </p>
                  <PreviewTable changes={data.changes} avgDeltaPct={data.avgDeltaPct} />
                </>
              ) : null}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={back} disabled={step === 0 || apply.isPending}>
              <ArrowLeft /> Atrás
            </Button>
            {step < 3 ? (
              <Button type="submit">
                {step === 2 ? 'Previsualizar' : 'Siguiente'} <ArrowRight />
              </Button>
            ) : (
              <Button
                type="button"
                disabled={!data || data.count === 0 || stale || preview.isPending || apply.isPending}
                onClick={() => setConfirmOpen(true)}
              >
                <Check /> Aplicar
              </Button>
            )}
          </div>
        </form>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={(o) => !apply.isPending && setConfirmOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              ¿Aplicar {data?.count === 1 ? '1 cambio' : `${data?.count ?? 0} cambios`} de precio?
            </DialogTitle>
            <DialogDescription>
              Los precios nuevos se ven enseguida en el menú público. Si te equivocás, lo podés deshacer desde el historial.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={apply.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              disabled={apply.isPending || !data || !previewBody}
              onClick={() => previewBody && data && apply.mutate({ body: previewBody, changes: data.changes })}
            >
              {apply.isPending && <Spinner />} Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Stepper({ step, onGoTo }: { step: number; onGoTo: (s: number) => void }) {
  return (
    <div className="grid gap-2">
      <p className="text-sm text-muted-foreground sm:hidden">
        Paso {step + 1} de {STEPS.length} · <span className="font-medium text-foreground">{STEPS[step]}</span>
      </p>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted sm:hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
      </div>
      <ol className="hidden items-center gap-2 sm:flex">
        {STEPS.map((label, i) => {
          const done = i < step;
          const current = i === step;
          return (
            <li key={label} className="flex flex-1 items-center gap-2">
              <button
                type="button"
                onClick={() => onGoTo(i)}
                disabled={!done}
                aria-current={current ? 'step' : undefined}
                className={cn(
                  'flex items-center gap-2 rounded-md text-sm whitespace-nowrap disabled:cursor-default',
                  current ? 'font-medium text-foreground' : done ? 'text-foreground hover:underline' : 'text-muted-foreground',
                )}
              >
                <span
                  className={cn(
                    'flex size-6 items-center justify-center rounded-full border text-xs',
                    current && 'border-primary bg-primary text-primary-foreground',
                    done && 'border-primary text-primary',
                  )}
                >
                  {done ? <Check className="size-3.5" /> : i + 1}
                </span>
                {label}
              </button>
              {i < STEPS.length - 1 && <span className={cn('h-px flex-1 bg-border', done && 'bg-primary')} />}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function PreviewError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const invalid = invalidTargets(error);
  if (invalid.length > 0) {
    return (
      <div className="grid gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
        <p className="flex items-center gap-2 font-medium">
          <AlertTriangle className="size-4 text-destructive" />
          {invalid.length === 1 ? 'Un precio quedaría en $ 0 o menos' : `${invalid.length} precios quedarían en $ 0 o menos`}
        </p>
        <ul className="grid gap-1 pl-6">
          {invalid.slice(0, 8).map((t) => (
            <li key={`${t.targetType}:${t.id}`} className="list-disc">
              {t.name}: {formatARS(t.oldPriceCents)} → {formatARS(t.newPriceCents)}
            </li>
          ))}
          {invalid.length > 8 && <li className="text-muted-foreground">y {invalid.length - 8} más…</li>}
        </ul>
        <p className="text-muted-foreground">Volvé atrás y ajustá el valor o el redondeo.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
      <span className="flex-1">{errorMessage(error)}</span>
      <Button type="button" size="sm" variant="outline" onClick={onRetry}>
        <RefreshCw /> Reintentar
      </Button>
    </div>
  );
}
